// DsPageHeader — implementa o cabeçalho de página do canvas (DESIGN-SYSTEM.md §3.1):
//   `h1` inline   em 132 dos 143 artboards -> prop `title`
//   `p.sub` inline em 132 artboards        -> prop `subtitle`
//   o slot de `.btnp`/`.btnpri` do cabeçalho -> prop `action`
//   a `.crumb` no corpo -> some daqui (no canvas a trilha vive na topbar)
//
// Proibido no lugar deste componente: `variant="h4"` (Public Sans 20px, 24px só a
// partir de `md`, sem letterSpacing), `variant="h1"` (Barlow 800, 40→64px) e
// `<CustomBreadcrumbs heading>` — `BreadcrumbsHeading` é um `styled('h6')` que faz
// `...theme.typography.h4`. Por isso o título aqui é um `Box component="h1"` com os
// valores literais do canvas, e não uma variante de tipografia do tema.
//
// Migração: troque o bloco inteiro de uma vez por arquivo. Feita pela metade, a tela
// fica com dois títulos. O `CustomBreadcrumbs` só permanece onde a trilha for requisito
// de navegação — e, nesse caso, sem a prop `heading`.

import type { SystemStyleObject } from '@mui/system';
import type { Theme, SxProps } from '@mui/material/styles';

import Box from '@mui/material/Box';

// ----------------------------------------------------------------------

export type DsPageHeaderProps = {
  title: string;
  subtitle?: React.ReactNode;
  /** Slot de ação à direita — tipicamente um `<DsButton>`. */
  action?: React.ReactNode;
  /** 68 artboards usam 700, 66 usam 800. Padrão: 700. */
  weight?: 700 | 800;
  sx?: SxProps<Theme>;
};

// ----------------------------------------------------------------------

/** Raiz: `display:flex;align-items:flex-start;justify-content:space-between;gap:12px;margin-bottom:16px`. */
const SX_ROOT = {
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  gap: '12px',
  mb: '16px',
} satisfies SystemStyleObject<Theme>;

/**
 * `<h1 style="font-family:var(--font-secondary);font-size:24px;letter-spacing:-0.02em;
 * margin:0;font-weight:700">` — literais de `loteamento.dc.html`. 78 artboards declaram
 * a família secundária explicitamente e os demais herdam; o kit sempre usa Barlow.
 */
const sxTitulo = (weight: 700 | 800) => (theme: Theme) => ({
  fontFamily: theme.typography.fontSecondaryFamily,
  fontSize: 24,
  fontWeight: weight,
  letterSpacing: '-0.02em',
  lineHeight: 1.5,
  m: 0,
});

/** `<p style="font-size:13px;color:var(--text-secondary);margin:4px 0 0">`. */
const SX_SUBTITULO = {
  fontSize: 13,
  color: 'text.secondary',
  m: '4px 0 0',
} satisfies SystemStyleObject<Theme>;

// ----------------------------------------------------------------------

export function DsPageHeader({ title, subtitle, action, weight = 700, sx }: DsPageHeaderProps) {
  return (
    <Box sx={[SX_ROOT, ...(Array.isArray(sx) ? sx : [sx])]}>
      {/* `minWidth:0` para que um título longo encolha em vez de empurrar a ação. */}
      <Box sx={{ minWidth: 0 }}>
        <Box component="h1" sx={sxTitulo(weight)}>
          {title}
        </Box>

        {subtitle ? (
          <Box component="p" sx={SX_SUBTITULO}>
            {subtitle}
          </Box>
        ) : null}
      </Box>

      {action ? <Box sx={{ flex: '0 0 auto' }}>{action}</Box> : null}
    </Box>
  );
}
